import { kr, signedKr } from '../../lib/money';
import { Kpi, RangeToggle } from '../bills/CostHistoryTotalCard';
import { periodRangeLabel, type RangeOption } from '../bills/cost-history';
import { Panel } from '../common/Panel';
import { ChartSkeleton } from '../common/Skeleton';
import { SavingsChart } from './SavingsChart';
import { averageNet, type SavingsHistory } from './savings-history';

/** Net saved per budget period across every pot, with this period, the average and the total. */
export function SavingsTotalCard({
  data,
  isLoading,
  range,
  onRangeChange,
}: {
  data: SavingsHistory | undefined;
  isLoading: boolean;
  range: RangeOption;
  onRangeChange: (range: RangeOption) => void;
}) {
  const totals = data?.totals ?? [];
  const current = totals.at(-1);
  const currentPeriod = data?.periods.at(-1);
  const average = averageNet(totals);
  const total = totals.reduce((sum, t) => sum + t.net, 0);
  const anyMovement = totals.some((t) => t.deposited > 0 || t.withdrawn > 0);

  return (
    <Panel
      title="Sparat per period"
      description="Det som flyttats till sparande, netto"
      action={<RangeToggle value={range} onChange={onRangeChange} />}
    >
      {isLoading || !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <ChartSkeleton height="2.75rem" />
            <ChartSkeleton height="2.75rem" />
            <ChartSkeleton height="2.75rem" />
          </div>
          <ChartSkeleton height="12rem" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Bottom-aligned so the numbers line up when a label wraps */}
          <div className="grid grid-cols-3 items-end gap-3">
            <Kpi
              label="Sparat denna period"
              value={current ? signedKr(current.net) : '–'}
              hint={currentPeriod ? periodRangeLabel(currentPeriod) : undefined}
            />
            <Kpi
              label="Snitt per period"
              value={average != null ? signedKr(average) : '–'}
              hint="hela perioder"
            />
            <Kpi label="Totalt" value={signedKr(total)} hint={`${range} perioder`} />
          </div>
          {anyMovement ? (
            <SavingsChart
              points={totals}
              average={average}
              height={{ base: 190, sm: 240 }}
              ariaLabel={`Sparat netto per period, senaste ${totals.length} perioderna. Totalt ${kr(total)}.`}
            />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Inget sparat de senaste {range} perioderna.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
